import { useState } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, RefreshCw, RotateCcw, Trash2, Truck } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Chip } from '@/components/ui'
import {
  usePickupDetail,
  useTransitionPickup,
  useRetryExtraction,
  useRegisterExternalFreight,
  useArchivePickup,
  useRestorePickup,
  type RegisterExternalFreightBody,
} from '../api/queries'
import type { Stage } from '../api/types'
import {
  STAGE_LABELS,
  STAGE_VARIANT,
  allowedTargetsFrom,
  isTerminal,
} from '../utils/stage-meta'
import { StageHistoryTimeline } from '../components/StageHistoryTimeline'
import { ConfirmCarrierBookingModal } from '../components/ConfirmCarrierBookingModal'

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function MaterialPickupDetailPage() {
  const params = useParams({ strict: false }) as { pickupId?: string }
  const pickupId = params.pickupId ?? ''
  const navigate = useNavigate()
  const detail = usePickupDetail(pickupId)
  const transition = useTransitionPickup()
  const retryExtraction = useRetryExtraction()
  const registerFreight = useRegisterExternalFreight()
  const archive = useArchivePickup()
  const restore = useRestorePickup()

  const [showBookingModal, setShowBookingModal] = useState(false)

  const pickup = detail.data

  if (detail.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-tertiary">Loading…</p>
      </div>
    )
  }

  if (!pickup) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-sm text-tertiary">Pickup not found.</p>
        <Button variant="secondary" size="sm" onClick={() => navigate({ to: '/material-pickup' })}>
          Back to inbox
        </Button>
      </div>
    )
  }

  const allowedTargets = allowedTargetsFrom(pickup.status)

  function doTransition(target: Stage) {
    transition.mutate(
      { pickupId: pickup!.id, targetStage: target },
      {
        onSuccess: () => {
          toast.success(`Moved to ${STAGE_LABELS[target]}`)
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'transition failed'
          toast.error(msg, { duration: 5000 })
        },
      },
    )
  }

  function handleTransition(target: Stage) {
    if (target === 'pickup_scheduled') {
      setShowBookingModal(true)
      return
    }
    doTransition(target)
  }

  async function handleBookingConfirm(body: RegisterExternalFreightBody) {
    try {
      await registerFreight.mutateAsync(body)
      toast.success('Shipment registered — carrier ref written to Odoo PO')
    } catch {
      toast.error('Failed to register shipment — stage not advanced', { duration: 6000 })
      setShowBookingModal(false)
      return
    }
    setShowBookingModal(false)
    doTransition('pickup_scheduled')
  }

  function handleBookingSkip() {
    setShowBookingModal(false)
    doTransition('pickup_scheduled')
  }

  function handleMarkDuplicate() {
    if (
      !window.confirm(
        'Mark this pickup as a duplicate? It will be removed from the pipeline board. ' +
          'Use this only when the same pickup already exists as another card — ' +
          'you can restore it later from the Duplicates view.',
      )
    ) {
      return
    }
    archive.mutate(
      { pickupId: pickup!.id, reason: 'duplicate' },
      {
        onSuccess: () => toast.success('Marked as duplicate — removed from board'),
        onError: () => toast.error('Could not mark as duplicate'),
      },
    )
  }

  function handleRestore() {
    restore.mutate(pickup!.id, {
      onSuccess: () => toast.success('Restored to pipeline'),
      onError: () => toast.error('Could not restore'),
    })
  }

  return (
    <>
    {showBookingModal ? (
      <ConfirmCarrierBookingModal
        pickup={pickup}
        onConfirm={handleBookingConfirm}
        onSkip={handleBookingSkip}
        onCancel={() => setShowBookingModal(false)}
        isPending={registerFreight.isPending || transition.isPending}
      />
    ) : null}
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <header className="flex flex-shrink-0 items-center gap-4 border-b border-border-subtle bg-surface px-6 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: '/material-pickup' })}
          aria-label="Back to inbox"
        >
          <ArrowLeft size={16} />
        </Button>
        <div className="flex flex-col">
          <h1 className="text-role-page-title m-0">
            {pickup.vendor_name ?? 'Material Pickup'}
          </h1>
          {pickup.odoo_po_names.length > 0 ? (
            <p className="text-role-page-subtitle m-0">{pickup.odoo_po_names.join(', ')}</p>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-3">
          {pickup.freight_shipment_id != null ? (
            <a
              href="/logistics"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 transition-colors"
            >
              <Truck size={13} />
              Track Shipment
            </a>
          ) : null}
          <Chip size="xs" variant={STAGE_VARIANT[pickup.status]} dot>
            {STAGE_LABELS[pickup.status]}
          </Chip>
          {pickup.deleted_at ? (
            <>
              <Chip size="xs" variant="neutral">Marked duplicate</Chip>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRestore}
                disabled={restore.isPending}
              >
                <RotateCcw size={14} className="mr-1.5" />
                Restore
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleMarkDuplicate}
              disabled={archive.isPending}
            >
              <Trash2 size={14} className="mr-1.5" />
              Mark as duplicate
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => detail.refetch()}
            disabled={detail.isFetching}
          >
            <RefreshCw size={14} />
          </Button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 gap-0">
        {/* Main detail panel */}
        <div className="flex flex-1 min-h-0 flex-col overflow-y-auto px-6 py-6 gap-6">
          {/* Fields */}
          <section className="rounded-lg border border-border-subtle bg-surface p-5">
            <h2 className="text-role-section-label mb-4">Pickup Details</h2>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div>
                <dt className="text-role-metadata mb-0.5">Vendor</dt>
                <dd className="text-role-data-primary m-0">
                  {pickup.vendor_name ?? <em className="text-tertiary">not extracted</em>}
                </dd>
              </div>
              <div>
                <dt className="text-role-metadata mb-0.5">Vendor contact</dt>
                <dd className="text-role-data-secondary m-0">
                  {pickup.vendor_contact_email ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-role-metadata mb-0.5">Odoo PO</dt>
                <dd className="text-role-data-primary m-0">
                  {pickup.odoo_po_names.length > 0
                    ? pickup.odoo_po_names.join(', ')
                    : <em className="text-tertiary">not linked</em>}
                </dd>
              </div>
              <div>
                <dt className="text-role-metadata mb-0.5">Carrier</dt>
                <dd className="text-role-data-secondary m-0">{pickup.carrier_name ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-role-metadata mb-0.5">Proposed pickup date</dt>
                <dd className="text-role-data-secondary m-0">
                  {formatDate(pickup.proposed_pickup_date)}
                </dd>
              </div>
              <div>
                <dt className="text-role-metadata mb-0.5">Pickup location</dt>
                <dd className="text-role-data-secondary m-0">{pickup.pickup_location ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-role-metadata mb-0.5">Received at</dt>
                <dd className="text-role-metadata m-0">{formatDate(pickup.created_at)}</dd>
              </div>
              <div>
                <dt className="text-role-metadata mb-0.5">Extraction</dt>
                <dd className="m-0 flex items-center gap-2">
                  <Chip
                    size="xs"
                    variant={
                      pickup.extraction_state === 'complete'
                        ? 'success'
                        : pickup.extraction_state === 'failed'
                          ? 'danger'
                          : 'neutral'
                    }
                  >
                    {pickup.extraction_state}
                  </Chip>
                  {pickup.extraction_state === 'failed' ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        retryExtraction.mutate(pickup.id, {
                          onError: () => toast.error('Retry failed'),
                        })
                      }
                      disabled={retryExtraction.isPending}
                    >
                      Retry
                    </Button>
                  ) : null}
                </dd>
              </div>
            </dl>
            {pickup.notes ? (
              <div className="mt-4 rounded bg-surface-sunk p-3 text-sm text-secondary">
                {pickup.notes}
              </div>
            ) : null}
          </section>

          {/* Line items */}
          {pickup.lines && pickup.lines.length > 0 ? (
            <section className="rounded-lg border border-border-subtle bg-surface p-5">
              <h2 className="text-role-section-label mb-4">Items</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle">
                    <th className="text-role-table-header py-2 text-left">Part #</th>
                    <th className="text-role-table-header py-2 text-left">Description</th>
                    <th className="text-role-table-header py-2 text-right">Qty</th>
                    <th className="text-role-table-header py-2 text-left">UOM</th>
                    <th className="text-role-table-header py-2 text-left">Matched product</th>
                  </tr>
                </thead>
                <tbody>
                  {pickup.lines.map((line) => (
                    <tr key={line.id} className="border-b border-border-subtle last:border-0">
                      <td className="text-role-table-cell py-2">
                        {line.parsed_part_number ?? '—'}
                      </td>
                      <td className="text-role-table-cell py-2">
                        {line.parsed_description ?? '—'}
                      </td>
                      <td className="text-role-table-cell py-2 text-right">
                        {line.parsed_qty != null ? line.parsed_qty : '—'}
                      </td>
                      <td className="text-role-table-cell py-2">
                        {line.parsed_uom ?? '—'}
                      </td>
                      <td className="text-role-table-cell py-2 text-secondary">
                        {line.matched_product_name ?? (
                          <em className="text-tertiary">unmatched</em>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}
        </div>

        {/* Right panel: stage controls + history */}
        <aside className="w-72 flex-shrink-0 overflow-y-auto border-l border-border-subtle bg-surface p-5">
          {/* Move stage */}
          {!isTerminal(pickup.status) || pickup.status !== 'received' ? (
            <section className="mb-6">
              <h3 className="text-role-section-label mb-3">Move pipeline</h3>
              <div className="flex flex-col gap-1.5">
                {allowedTargets.map((target) => (
                  <Button
                    key={target}
                    variant="secondary"
                    size="sm"
                    className="justify-start"
                    onClick={() => handleTransition(target)}
                    disabled={transition.isPending}
                  >
                    <Chip size="xs" variant={STAGE_VARIANT[target]} dot className="mr-2" />
                    {STAGE_LABELS[target]}
                  </Button>
                ))}
              </div>
            </section>
          ) : null}

          {/* Stage history */}
          <section>
            <h3 className="text-role-section-label mb-3">Stage history</h3>
            <StageHistoryTimeline entries={pickup.stage_history ?? []} />
          </section>
        </aside>
      </div>
    </div>
    </>
  )
}
